import { createCardToken } from "@mercadopago/sdk-react/esm/coreMethods";
import { getPaymentMethods } from "@mercadopago/sdk-react";

export interface CardData {
  cardExpirationMonth: string;
  cardExpirationYear: string;
  cardholderName: string;
  cardNumber: string;
  securityCode: string;
  // identificationNumber: string; // CPF number
}

export interface CardTokenResponse {
  id: string;
  [key: string]: any;
}

/**
 * Generate a card token using MercadoPago SDK
 * @param cardData - Card data to tokenize
 * @returns Generated token
 */
export const generateCardToken = async (
  cardData: CardData
): Promise<CardTokenResponse> => {
  try {
    const token = await createCardToken({
      cardExpirationMonth: cardData.cardExpirationMonth,
      cardExpirationYear: cardData.cardExpirationYear,
      cardholderName: cardData.cardholderName,
      cardNumber: cardData.cardNumber,
      securityCode: cardData.securityCode,
      // identificationType: 'CPF',
      // identificationNumber: cardData.identificationNumber,
    });

    return token as CardTokenResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * Generate a card token using an existing saved card ID
 * @param cardId - ID of the saved card
 * @param securityCode - Security code (CVV) entered by user
 * @returns Generated token
 */
export const generateCardTokenFromCardId = async (
  cardId: string,
  securityCode: string
): Promise<CardTokenResponse> => {
  try {
    const token = await createCardToken({
      cardId: cardId,
      securityCode: securityCode,
    });

    return token as CardTokenResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * Resolve issuerId from a card BIN using MercadoPago SDK
 * @param cardNumber - Full or partial card number (will extract first 6 digits)
 * @param type - 'credit_card' or 'debit_card'
 */
export const getIssuerIdFromCardNumber = async (
  cardNumber: string,
  type: "credit_card" | "debit_card" = "credit_card"
): Promise<number | null> => {
  try {
    const bin = (cardNumber || "").replace(/\D/g, "").slice(0, 6);
    if (bin.length !== 6) return null;
    const result: any = await getPaymentMethods({ bin });
    const methods = result?.results || [];
    const method =
      methods.find((m: any) => m?.payment_type_id === type) || methods[0];
    const issuerId = method?.issuer?.id;
    return typeof issuerId === "number"
      ? issuerId
      : issuerId
      ? Number(issuerId)
      : null;
  } catch (e) {
    return null;
  }
};

/**
 * Resolve MercadoPago payment method id (e.g., 'master', 'visa') from card BIN
 */
export const getPaymentMethodIdFromCardNumber = async (
  cardNumber: string,
  type: "credit_card" | "debit_card" = "credit_card"
): Promise<string | null> => {
  try {
    const bin = (cardNumber || "").replace(/\D/g, "").slice(0, 6);
    if (bin.length !== 6) return null;
    const result: any = await getPaymentMethods({ bin });
    const methods = result?.results || [];
    const method =
      methods.find((m: any) => m?.payment_type_id === type) || methods[0];
    return method?.id || null;
  } catch (e) {
    return null;
  }
};
